// D-191 — the shell's own log, and the service §11.13's Console reads.
//
// Hangar has always *had* diagnostics; it had nowhere to put them. The bridge's frame log lives
// in the App Inspector (§11.12), the Notification Center holds what apps announced (§13), and
// everything else — a sync that failed, a quota warning, a session restore that skipped windows,
// a `TypeError` thrown out of a built-in — went to `console.log` and off the end of a tab nobody
// had open. On a deployed desktop that is the difference between "a user says it broke" and
// knowing what broke.
//
// Three things this is deliberately **not**:
//
//   • **Not a server log.** D-028 is unambiguous that Hangar executes nothing server-side and
//     owns no containers; the ops sources it would be nice to see (a deploy, a service, a
//     workspace container) belong to whoever owns that blast radius. This service takes
//     *sources*, and one of them can later be an app streaming its own — which is §11.2's
//     `CommandProvider` shape reused, not a new idea.
//   • **Not a second event bus.** It subscribes to §4.2's bus and never emits on it. Nothing in
//     the kernel reacts to a log entry, which is what keeps this observational: turning logging
//     off can never change behavior.
//   • **Not persistent.** A ring in memory, gone on reload, exactly like a browser's own console.
//     Writing it to the VFS would put a growing file inside the thing it is meant to observe —
//     the Console can *export* on demand instead (§11.13), which is the same capability without
//     the feedback loop.
import type { Bus } from '../bus';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** The built-in sources. An app-provided source adds its own id at runtime. */
export type LogSourceId = 'shell' | 'apps' | 'files' | 'bridge' | 'notify';

export interface LogEntry {
  /** Monotonic within one session, and the stable identity for a row in a virtualised list. */
  seq: number;
  at: number;
  source: string;
  level: LogLevel;
  /** The app an entry is *about*, when it is about one. Not who wrote it. */
  appId?: string;
  message: string;
  /** One extra line, already flattened. Never an object — the Console renders text. */
  detail?: string;
}

/**
 * §11.13 — how many entries the ring holds.
 *
 * 2000 is roughly a working day of an idle desktop and a few minutes of a busy one, and at the
 * ~150 bytes an entry costs that is a third of a megabyte. The Center's 100 (§13) is the wrong
 * order of magnitude here: a notification is something a user should read, a log line is
 * something a user should be able to *scroll back to*.
 */
export const LOG_LIMIT = 2_000;

export interface LogsService {
  /** Everything held, oldest first. */
  entries(): readonly LogEntry[];
  /** Append one entry. Returns its `seq`. */
  log(input: {
    source: string;
    level: LogLevel;
    message: string;
    appId?: string;
    detail?: string;
  }): number;
  /** Which sources have produced at least one entry, in first-seen order. */
  sources(): readonly string[];
  clear(): void;
  subscribe(onChange: () => void): () => void;
  /**
   * Subscribe to §4.2's bus and start recording. Returns the teardown, which the host owns —
   * the service is constructible without a bus so its own tests do not need one.
   */
  attach(bus: Bus): () => void;
}

export interface LogsServiceDeps {
  now?: () => number;
  limit?: number;
}

export function createLogsService(deps: LogsServiceDeps = {}): LogsService {
  const now = deps.now ?? (() => Date.now());
  const limit = deps.limit ?? LOG_LIMIT;
  const ring: LogEntry[] = [];
  const seen: string[] = [];
  const listeners = new Set<() => void>();
  let seq = 0;

  const emit = (): void => {
    for (const listener of [...listeners]) listener();
  };

  const push = (input: {
    source: string;
    level: LogLevel;
    message: string;
    appId?: string;
    detail?: string;
  }): number => {
    seq += 1;
    const entry: LogEntry = {
      seq,
      at: now(),
      source: input.source,
      level: input.level,
      message: input.message,
      ...(input.appId === undefined ? {} : { appId: input.appId }),
      ...(input.detail === undefined ? {} : { detail: input.detail }),
    };
    ring.push(entry);
    // Splice rather than shift-per-entry: a burst (a sync of 500 files) would otherwise walk
    // the whole array once per line.
    if (ring.length > limit) ring.splice(0, ring.length - limit);
    if (!seen.includes(entry.source)) seen.push(entry.source);
    emit();
    return seq;
  };

  return {
    entries: () => ring,
    log: push,
    sources: () => seen,

    clear() {
      ring.length = 0;
      // `seen` survives: a source that has spoken once is still a source, and emptying the
      // filter chips out from under a user mid-investigation is the wrong behavior for "clear".
      emit();
    },

    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },

    attach(bus) {
      const offs: Array<() => void> = [
        // ── shell ────────────────────────────────────────────────────────────────────────
        bus.on('settings:skinChanged', ({ skin }) =>
          push({ source: 'shell', level: 'info', message: `Skin changed to ${skin}` }),
        ),
        bus.on('settings:themeChanged', ({ theme }) =>
          push({ source: 'shell', level: 'debug', message: `Theme changed to ${theme}` }),
        ),
        bus.on('session:restored', ({ windows }) =>
          push({
            source: 'shell',
            level: 'info',
            message: `Session restored — ${windows} window${windows === 1 ? '' : 's'}`,
          }),
        ),

        // ── apps ─────────────────────────────────────────────────────────────────────────
        bus.on('app:installed', ({ appId }) =>
          push({ source: 'apps', level: 'info', appId, message: `Installed ${appId}` }),
        ),
        bus.on('app:uninstalled', ({ appId }) =>
          push({ source: 'apps', level: 'info', appId, message: `Uninstalled ${appId}` }),
        ),
        bus.on('app:launched', ({ appId, winId }) =>
          push({ source: 'apps', level: 'debug', appId, message: `Launched ${appId}`, detail: winId }),
        ),
        bus.on('app:healthChanged', ({ appId, healthy }) =>
          push({
            source: 'apps',
            // §9.6 dims the icon on the unhealthy transition; this is the line that says why.
            level: healthy ? 'info' : 'warn',
            appId,
            message: healthy ? `${appId} is reachable again` : `${appId} stopped answering`,
          }),
        ),
        bus.on('app:unloaded', ({ appId }) =>
          push({ source: 'apps', level: 'debug', appId, message: `Unloaded ${appId} to reclaim memory` }),
        ),
        bus.on('app:resumed', ({ appId }) =>
          push({ source: 'apps', level: 'debug', appId, message: `Resumed ${appId}` }),
        ),

        // ── files ────────────────────────────────────────────────────────────────────────
        bus.on('fs:quotaWarning', ({ usedBytes, quotaBytes }) =>
          push({
            source: 'files',
            level: 'warn',
            message: 'Storage is nearly full',
            detail: `${usedBytes} of ${quotaBytes} bytes used`,
          }),
        ),
        bus.on('fs:quotaExceeded', ({ path }) =>
          push({ source: 'files', level: 'error', message: 'Write failed — out of room', detail: path }),
        ),
        bus.on('fs:conflict', ({ path, copyPath }) =>
          push({
            source: 'files',
            level: 'warn',
            message: 'Sync conflict — both versions kept',
            detail: `${path} → ${copyPath}`,
          }),
        ),
        bus.on('fs:syncStateChanged', ({ online, pending }) =>
          push({
            source: 'files',
            level: online ? 'info' : 'warn',
            message: online ? 'Sync online' : 'Sync offline — changes are queued',
            detail: `${pending} pending`,
          }),
        ),

        // ── bridge (§9.3) ────────────────────────────────────────────────────────────────
        //
        // Only the *failures*. Every frame already goes to the Inspector's log, and mirroring
        // that volume here would bury everything else — a busy app is thousands of frames a
        // minute. A drop is the line worth keeping.
        bus.on('bridge:drop', ({ reason }) =>
          push({ source: 'bridge', level: 'warn', message: `Bridge message dropped: ${reason}` }),
        ),
        bus.on('bridge:unrouted', ({ instanceKey, key }) =>
          push({
            source: 'bridge',
            level: 'debug',
            message: `Unrouted channel: ${key}`,
            detail: instanceKey,
          }),
        ),

        // ── notify (§13) ─────────────────────────────────────────────────────────────────
        bus.on('notify:posted', ({ id, appId }) =>
          push({ source: 'notify', level: 'info', appId, message: `Notification posted`, detail: id }),
        ),
      ];

      return () => {
        for (const off of offs) off();
      };
    },
  };
}
